import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectFilteredVirtualCards,
  selectUsersFullPaginatedDataFromState,
  selectVirtualCardsFullPaginatedData,
} from 'src/app/YourCardsActive/redux/yourCardsActive.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsFiltersBody,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { VirtualCardStatus } from 'src/models/virtualCard.models'
import { postReportVirtualCardsDownload } from 'src/redux/actions/data/reportDownloads.actions'
import { getVirtualCards } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectVirtualCardsActiveOrPendingRecipientMeCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const YourCardsActive: React.FC = () => {
  const userId = useSelector(selectUserIdFromState)
  const virtualCardsFullPaginatedData = useSelector(
    selectVirtualCardsFullPaginatedData,
  )
  const { analytics } = useAnalyticsContext()
  const filteredVirtualCards = useSelector(selectFilteredVirtualCards)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsActiveOrPendingRecipientMeCount = useSelector(
    selectVirtualCardsActiveOrPendingRecipientMeCountStatisticsFromState,
  )
  const usersFullPaginatedData = useSelector(
    selectUsersFullPaginatedDataFromState,
  )
  const appName = useSelector(selectAppName)

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.VirtualCardsActiveOrPendingRecipientMeCountStatistics,
    ])
  }, [dispatchStatistics])

  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    dispatch(
      postReportVirtualCardsDownload({
        ...filters,
        recipient: userId,
        statuses: [VirtualCardStatus.Active, VirtualCardStatus.Pending],
      }),
    )
    analytics?.track(
      'your cards - download all virtual cards active and pending - text link press',
      {
        screen: '/virtualcards',
        intent: 'download all virtual cards active and pending',
        interaction: 'text link press',
      },
    )
  }

  const fetchVirtualCards = (
    filtersBody?: VirtualCardsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getVirtualCards({
        count: 10,
        page: page,
        ...filtersBody,
        recipient: userId,
        statuses: [VirtualCardStatus.Active, VirtualCardStatus.Pending],
      }),
    )
  }

  const infiniteScrollProps = useMemo(
    () => ({
      ...virtualCardsFullPaginatedData,
      fetch: fetchVirtualCards,
    }),
    [virtualCardsFullPaginatedData, fetchVirtualCards],
  )

  return !statisticsLoaded.virtualCardsActiveOrPendingRecipientMeCountStatisticsLoaded ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Your Active & Pending Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`Your Active & Pending Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          onDownloadClick={dispatchPostReportVirtualCardsDownload}
          defaultTitle={
            <ObjectBoxTitle
              static={{
                label: <Trns source={'Virtual Cards'} />,
                count: virtualCardsActiveOrPendingRecipientMeCount,
              }}
            />
          }
          virtualCards={filteredVirtualCards}
          emptyStateDesign={TableEmptyStateDesigns.YourVirtualCardsActive}
          hideFilters
          infiniteScrollProps={infiniteScrollProps}
          initialFilterValues={{
            sortBy: 'pendingUpdatedAt',
          }}
          totalCount={virtualCardsActiveOrPendingRecipientMeCount}
          usersFullPaginatedData={usersFullPaginatedData}
        />
      </BackgroundPageContent>
    </>
  )
}

export default YourCardsActive
